import { Suspense, lazy } from 'react';
import type { ComponentType } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { WagmiProvider } from 'wagmi';
import { Spinner } from './design/Spinner.js';
import { Landing } from './landing/Landing.js';
import { DataDeletionPage } from './legal/DataDeletionPage.js';
import { DeletionStatusPage } from './legal/DeletionStatusPage.js';
import { PrivacyPage } from './legal/PrivacyPage.js';
import { TermsPage } from './legal/TermsPage.js';
import { queryClient } from './lib/queryClient.js';
import { wagmiConfig } from './lib/wagmi.js';

/**
 * The dashboard lives in src/app/** and is owned by another part of the build.
 * `import.meta.glob` resolves it at bundle time when the module exists and
 * yields an empty map when it does not, so the web app compiles either way.
 */
const dashboardModules: Record<string, () => Promise<unknown>> = {
  ...import.meta.glob('./app/routes.{ts,tsx}'),
  ...import.meta.glob('./app/routes/index.{ts,tsx}'),
};

interface DashboardModule {
  default?: ComponentType;
  DashboardRoutes?: ComponentType;
  AppRoutes?: ComponentType;
}

function DashboardMissing() {
  return (
    <div className="min-h-screen bg-paper text-ink flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-panel px-6 py-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">Dashboard</div>
        <h1 className="mt-3 font-serif text-2xl leading-tight tracking-tight">The dashboard is not built yet</h1>
        <p className="mt-3 text-sm text-ink/70 leading-relaxed">
          <code className="text-ink">src/app/routes</code> is not present in this build. The public
          site works without it.
        </p>
        <a
          href="/"
          className="mt-6 inline-flex px-5 py-2.5 bg-ink text-paper text-sm font-medium rounded-lg hover:bg-ink/90 dark:bg-accent dark:text-paper dark:hover:bg-accent-hover transition-colors duration-200"
        >
          Back to the site
        </a>
      </div>
    </div>
  );
}

const Dashboard = lazy(async () => {
  const loaders = Object.values(dashboardModules);
  const load = loaders[0];
  if (!load) return { default: DashboardMissing };
  try {
    const module = (await load()) as DashboardModule;
    const component = module.default ?? module.DashboardRoutes ?? module.AppRoutes;
    return { default: component ?? DashboardMissing };
  } catch {
    return { default: DashboardMissing };
  }
});

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="md" label="Loading" className="text-ink/40" />
    </div>
  );
}

/**
 * WagmiProvider wraps the existing QueryClientProvider rather than replacing it:
 * wagmi needs a react-query client and this app already has exactly one, with
 * the retry policy the API deserves. Two clients would mean two caches.
 */
export function App() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            {/*
              The public documents. Three of these URLs are mandatory fields on
              the Meta app and are checked by a reviewer while logged out, so
              they are part of the site's own bundle rather than the dashboard's.
            */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/deletion-status/:code" element={<DeletionStatusPage />} />
            <Route
              path="/app/*"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <Dashboard />
                </Suspense>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default App;
